import {Box, CircularProgress, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useClientLocator} from "../../../../client/ApiClientLocator";
import {PaymentTranslKey} from "../../PaymentTranslKey";
import {PaymentInformationDetails} from "./PaymentInformationDetails";
import {usePaymentInformationQuery} from "./PaymentInformationQuery";
import {usePaymentRequiredQuery} from "./RequirePaymentQuery";

interface PaymentInformationPanelProps {
    submissionId: number;
    authorizationSucceeded: boolean;
}

export function PaymentInformationPanel({submissionId, authorizationSucceeded}: Readonly<PaymentInformationPanelProps>) {
    const {t} = useTranslation();
    const {submissionClient, paymentInformationApiClient} = useClientLocator();
    const {
        data: submissionWithPaymentRequired,
        isLoading: isLoadingPaymentRequired,
        isError: hasPaymentRequiredError
    } = usePaymentRequiredQuery(submissionId, submissionClient, authorizationSucceeded);

    const {data: paymentInformation, isError: hasPaymentDataError} = usePaymentInformationQuery(
        submissionId,
        submissionWithPaymentRequired?.paymentRequired,
        paymentInformationApiClient,
        authorizationSucceeded
    );

    if (hasPaymentRequiredError || hasPaymentDataError) {
        return (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="200px" gap={2}>
                <Typography variant="body1">{t(PaymentTranslKey.PAYMENT_INFORMATION_ERROR)}</Typography>
            </Box>
        );
    }

    if (isLoadingPaymentRequired || !submissionWithPaymentRequired) {
        return (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="200px" gap={2}>
                <CircularProgress />
                <Typography variant="body1">{t(PaymentTranslKey.PAYMENT_INFORMATION_LOADING)}</Typography>
            </Box>
        );
    }

    if (submissionWithPaymentRequired.paymentRequired === false) {
        return null;
    }

    if (submissionWithPaymentRequired.paymentRequired === true) {
        if (!paymentInformation) {
            return (
                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="200px" gap={2}>
                    <CircularProgress />
                    <Typography variant="body1">{t(PaymentTranslKey.PAYMENT_INFORMATION_LOADING)}</Typography>
                </Box>
            );
        }

        return <PaymentInformationDetails paymentInformation={paymentInformation} />;
    }

    return null;
}
